import {
  type ClientOptions,
  type ProfileKey,
  sapi,
  type SapiClient,
  type SearchParameters
} from '@findhotel/sapi'
import {getAnonymousId, getSeoActiveAttributes} from 'modules/meta/selectors'
import {storeSapiConfig} from 'modules/meta/slice'
import {runHotelsSearch} from 'modules/sapiSearch/actions'
import {AnchorNotFoundError, isAnySearchError} from 'modules/sapiSearch/errors'
import {
  getAnchorCountryPlaceId,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {seoApi} from 'modules/seoApi'
import {
  preloadSeoAlternateLinks,
  preloadSeoBreadcrumb,
  preloadSeoLinksFAQ,
  preloadSeoLinksMetadata,
  preloadSeoPopularLinks,
  preloadSeoRelatedLinks,
  preloadSeoTopHotels
} from 'modules/seoApi/preload'
import {
  COUNTRY_TOP_HOTELS_LIMIT,
  getIsSeoContentReady,
  getSeoBreadcrumbData
} from 'modules/seoApi/selectors'
import {
  preloadTrustpilotBusinessUnit,
  shouldPreloadTrustpilot
} from 'modules/trustpilot/preload'
import {mapObjIndexed, pick, pickBy, pipe} from 'ramda'
import {initializeStore, type RootState, Store} from 'store'
import {createDeferredPromise} from 'utils/promise/deferredPromise'
import {
  baseGetServerSideProps,
  CommonPageProps
} from 'utils/server/defaultGetServerSideProps'
import {getExperimentSegmentationAttributes} from 'utils/server/getExperimentSegmentationAttributes'
import {getServerSapiClientOptions} from 'utils/server/getServerSapiClientOptions'
import {getServerSearchDecision} from 'utils/server/getServerSearchDecision'
import {
  hasPreloadedReduxState,
  isClientSideNavigation,
  isRedirectOrNotFound
} from 'utils/server/getServerSidePropsUtils'
import {getUserAgentFromServerContext} from 'utils/server/getUserAgentFromServerContext'
import {logPreloadError} from 'utils/server/logPreloadError'
import {incrementMetric} from 'utils/server/metrics'
import {preloadAnchorSearch} from 'utils/server/preloadAnchorSearch'
import {preloadBaseSearchState} from 'utils/server/preloadBaseSearchState'
import {getUrlObjectFromContext} from 'utils/server/searchParameters/utils/generic'
import {getSeoSRPPath} from 'utils/server/searchSeo/getSeoSRPPath'
import {serverSideTimeouts} from 'utils/server/serverSideTimeouts'
import {getHomeLinkWithUTMParams} from 'utils/utmParams'
import SearchView from 'views/search/SearchContainer'

import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {createServerToggle} from '@vio/core/src/_web/experiments/createServerToggle'
import {Page} from '@vio/core/src/analytics/types/Events'
import {getBrandHasSeoSrpFlow} from '@vio/core/src/brand/utils'
import {trustpilotApi} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {removeEmptyRecursive} from '@vio/core/src/utils/object'

import type {AnchorPreheatOptions} from 'modules/sapiSearch/partialSearch'
import type {GetServerSideProps, Redirect} from 'next'

export interface PageProps extends CommonPageProps {
  searchParameters?: SearchParameters
  sapiClientOptions?: ClientOptions
  userIp?: string
  isEligibleForServerSearchExperiment?: boolean
  preloadedAnchorOptions?: AnchorPreheatOptions
}

/**
 * Select only the slices we want to provide and remove undefined values for serialization
 */
const sanitizeState = pipe<
  [RootState],
  DeepPartial<RootState>,
  DeepPartial<RootState>
>(
  pick([
    'meta',
    'experiments',
    'sapiSearch',
    'search',
    'searchBox',
    'seoApi',
    'trustpilotApi'
  ]),
  mapObjIndexed(removeEmptyRecursive)
)

export default SearchView

export const getServerSideProps: GetServerSideProps<
  PageProps
> = async context => {
  const {
    params: pathParams,
    req: {cookies, headers}
  } = context

  const isSeoSRP = Boolean(pathParams?.taxonomy)

  const result = await baseGetServerSideProps({
    page: Page.Search,
    isCacheable: isSeoSRP
  })(context)

  if (
    isRedirectOrNotFound(result) ||
    isClientSideNavigation(context) ||
    !hasPreloadedReduxState(result.props)
  ) {
    return result
  }

  const state = result.props.preloadedReduxState as RootState
  const brand = getBrand(state)
  const seoAttributes = getSeoActiveAttributes(state)
  const urlObject = getUrlObjectFromContext(context, brand)
  const queryParams = urlObject.searchParams
  const queryString = urlObject.searchParams.toString()

  // No anonymousId = cached page, we cannot run toggle or dynamic SSR
  const anonymousId = getAnonymousId(state)
  if (!anonymousId && !isSeoSRP) return result

  // We only support SEO-friendly URLs for Vio brand
  if (isSeoSRP && !getBrandHasSeoSrpFlow(brand)) {
    return {notFound: true}
  }

  const {experiments} = result.props
  const attributes = getExperimentSegmentationAttributes(state, urlObject)
  const toggle = createServerToggle(anonymousId, attributes, experiments)

  const {
    shouldSearchOnServer,
    shouldPreloadAnchor,
    isEligibleForServerSearchExperiment,
    useVisitorCountryRouting
  } = getServerSearchDecision({isSeoSRP, queryParams, cookies, toggle})

  const metricTags = {
    is_seo_srp: String(isSeoSRP)
  }

  let store: Store
  let preloadedAnchorOptions: AnchorPreheatOptions | undefined
  try {
    // Create a promise that allows us to await the async LOV response from inside a callback
    const sapiConfig = createDeferredPromise()

    // We create a single SAPI client (if we need one), consumers can await the promise to get it
    let sapiClientPromise: Promise<SapiClient>
    const getSapiClient = () => {
      if (!sapiClientPromise) {
        sapiClientPromise = sapi(
          process.env.NEXT_PUBLIC_SAPI_PROFILE_KEY as ProfileKey,
          {
            ...getServerSapiClientOptions(store.getState(), toggle, isSeoSRP),
            userAgent: getUserAgentFromServerContext(context),
            callbacks: {
              onConfigReceived: configs => {
                store.dispatch(storeSapiConfig(configs))
                sapiConfig.resolve()
              }
            }
          }
        )
      }
      return serverSideTimeouts.sapiClient(sapiClientPromise)
    }

    store = initializeStore(state, {toggle, getSapiClient})
    const userIp = (headers['x-client-ip'] as string) ?? null

    await serverSideTimeouts.baseState(
      preloadBaseSearchState({
        store,
        context,
        disallowInvalidSearchParams: true,
        pageName: Page.Search
      })
    )

    const searchParameters = getSearchParameters(store.getState())

    if (shouldPreloadAnchor) {
      const options = {
        ...getServerSapiClientOptions(store.getState(), toggle, false),
        userAgent: getUserAgentFromServerContext(context)
      }

      // Preheat must use the same regional routing as browser offers and AP rooms.
      if (useVisitorCountryRouting) delete options.forcedRegion

      await preloadAnchorSearch(store, searchParameters, options, userIp)

      // Pass only browser-safe options so continuation keeps the server context.
      // Drop undefined values because Next.js serializes these page props.
      preloadedAnchorOptions = pickBy(
        value => value !== undefined,
        pick(
          [
            'anonymousId',
            'language',
            'currency',
            'brand',
            'userCountry',
            'deviceType',
            'pageSize',
            'variations',
            'forcedRegion'
          ],
          options
        )
      )
    } else if (shouldSearchOnServer) {
      const hotelsSearch = serverSideTimeouts.hotelsSearch(
        store.dispatch(runHotelsSearch({searchParameters, userIp}))
      )

      const seoPreloads = () =>
        isSeoSRP
          ? serverSideTimeouts.seoPreload(
              Promise.all([
                store.dispatch(preloadSeoBreadcrumb(pathParams?.placeId)),
                store.dispatch(preloadSeoAlternateLinks(pathParams?.taxonomy)),
                store.dispatch(preloadSeoRelatedLinks(pathParams?.taxonomy)),
                store.dispatch(preloadSeoPopularLinks()),
                store.dispatch(preloadSeoLinksMetadata(pathParams?.taxonomy)),
                store.dispatch(preloadSeoLinksFAQ(pathParams?.taxonomy)),
                store.dispatch(preloadSeoTopHotels(pathParams?.placeId))
              ])
            )
          : Promise.resolve()

      const trustpilotPreload = () =>
        shouldPreloadTrustpilot(store.getState())
          ? serverSideTimeouts.trustpilotPreload(
              store.dispatch(preloadTrustpilotBusinessUnit())
            )
          : Promise.resolve()

      // The country tab needs the anchor from search results
      const countryTopHotelsPreload = hotelsSearch.then(
        () => {
          if (!isSeoSRP) return

          const countryPlaceId = getAnchorCountryPlaceId(store.getState())
          if (!countryPlaceId || countryPlaceId === pathParams?.placeId) return

          return serverSideTimeouts.seoPreload(
            store.dispatch(
              preloadSeoTopHotels(countryPlaceId, COUNTRY_TOP_HOTELS_LIMIT)
            )
          )
        },
        () => undefined // A failed search is reported through hotelSearchResult, so it is ignored here.
      )

      const [
        hotelSearchResult,
        seoPreloadResult,
        countryTopHotelsResult,
        trustpilotResult
      ] = await Promise.allSettled([
        hotelsSearch,
        seoPreloads(),
        countryTopHotelsPreload,
        trustpilotPreload()
      ])

      if (seoPreloadResult.status === 'rejected') {
        logPreloadError('Error preloading SEO data:', seoPreloadResult.reason)
        incrementMetric('ssr.srp.preload_error', metricTags)
      }

      if (trustpilotResult.status === 'rejected') {
        logPreloadError(
          'Error preloading Trustpilot business unit:',
          trustpilotResult.reason
        )
        incrementMetric('ssr.srp.preload_error', {
          ...metricTags,
          preload: 'trustpilot'
        })
      }

      if (countryTopHotelsResult.status === 'rejected') {
        logPreloadError(
          'Error preloading country top hotels:',
          countryTopHotelsResult.reason
        )
        incrementMetric('ssr.srp.preload_error', metricTags)
      }

      if (hotelSearchResult.status === 'rejected') {
        throw hotelSearchResult.reason
      }

      // We need LOV to render the SRP, not for searching
      await serverSideTimeouts.sapiConfig(sapiConfig.promise)

      // Double check SEO URL in case landing in the the Seo SRP
      if (isSeoSRP) {
        const breadcrumb = getSeoBreadcrumbData(
          store.getState(),
          pathParams?.placeId
        )
        const correctPath = getSeoSRPPath({
          pathParams,
          seoAttributes,
          breadcrumb
        })

        // Avoids redirect in case the SEO Path is not correctly generated
        if (correctPath && correctPath !== urlObject.pathname) {
          return {
            redirect: {
              destination: correctPath,
              permanent: false // 307 redirect
            }
          }
        }

        const isSeoContentReady = getIsSeoContentReady(
          store.getState(),
          pathParams?.taxonomy
        )

        /**
         * max-age=0 — browsers never cache, always revalidate via CloudFront.
         * s-maxage — CloudFront freshness window. 2d when SEO metadata + FAQ are generated, 1d when still pending.
         * stale-while-revalidate=2592000 — up to 30d of stale responses while the background revalidation runs.
         */
        const sMaxAge = isSeoContentReady ? 172800 : 86400
        context.res.setHeader(
          'Cache-Control',
          `max-age=0, s-maxage=${sMaxAge}, stale-while-revalidate=2592000`
        )
      }
    }

    result.props.preloadedReduxState = sanitizeState(store.getState())
    incrementMetric('ssr.srp.completed', metricTags)
  } catch (error) {
    if (isAnySearchError(error)) {
      if (error instanceof AnchorNotFoundError) {
        incrementMetric('ssr.srp.anchor_not_found', metricTags)
      }
      incrementMetric('ssr.srp.home_redirect', metricTags)

      const destination = getHomeLinkWithUTMParams(queryString, brand, false)
      return {
        redirect: {destination, permanent: false} as Redirect
      }
    }

    // Unhandled errors log & fall through to client-side search
    logPreloadError('Error preloading search:', error)
    incrementMetric('ssr.srp.csr_fallback', metricTags)

    preloadedAnchorOptions = undefined

    // Reset to avoid sending partial SSR state
    result.props.preloadedReduxState = state
  } finally {
    // Avoid memory leaks from preloads - https://redux-toolkit.js.org/rtk-query/usage/server-side-rendering#server-side-rendering-with-nextjs
    if (store) {
      store.dispatch(seoApi.util.resetApiState())
      store.dispatch(trustpilotApi.util.resetApiState())
    }
  }

  return {
    props: {
      ...result.props,
      isEligibleForServerSearchExperiment,
      ...(preloadedAnchorOptions ? {preloadedAnchorOptions} : {})
    }
  }
}
